import { Fragment } from "react";

import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { Form } from "@/components/Form/Form";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { SectionCard } from "@/design-system/SectionCard";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { useTranslation } from "@/translations";

import type { BulkOperationResult } from "./types";

export type BulkOperationReviewField = {
  label: string;
  value: string;
};

type BulkOperationReviewProps = {
  title: string;
  description: string;
  fields: BulkOperationReviewField[];
  validProjects: Array<{ id: string; name: string }>;
  skippedProjects: Array<{ id: string; name: string; reason: string }>;
  isApplying: boolean;
  onBack: () => void;
  onApply: () => void;
};

export function BulkOperationReview({
  title,
  description,
  fields,
  validProjects,
  skippedProjects,
  isApplying,
  onBack,
  onApply,
}: BulkOperationReviewProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <ConfirmModal
      tone="primary"
      isOpened
      title={title}
      description={description}
      isLoading={isApplying}
      onOpenChange={(isOpened) => !isOpened && onBack()}
      onReject={onBack}
      onResolve={onApply}
      rejectBtnProps={{ text: t("common.action.back") }}
      resolveBtnProps={{
        text: t("page.portfolio-integrations.review.apply", { count: validProjects.length }),
        hide: validProjects.length === 0,
      }}
    >
      <Stack gap="md">
        <Inline gap="sm">
          <Badge variant="success">
            {t("page.portfolio-integrations.review.will-change", { count: validProjects.length })}
          </Badge>
          <Badge variant={skippedProjects.length ? "warning" : "default"}>
            {t("page.portfolio-integrations.review.will-skip", { count: skippedProjects.length })}
          </Badge>
        </Inline>

        {fields.length > 0 && (
          <Stack gap="sm">
            <h3 className="text-caption-emphasis">{t("page.portfolio-integrations.review.fields")}</h3>
            <dl className="grid grid-cols-2 gap-x-space-md gap-y-space-sm text-caption">
              {fields.map((field) => (
                <Fragment key={field.label}>
                  <dt className="text-text-secondary">{field.label}</dt>
                  <dd className="text-caption-emphasis wrap-break-word">{field.value}</dd>
                </Fragment>
              ))}
            </dl>
          </Stack>
        )}

        {skippedProjects.length > 0 && (
          <Notice
            variant="warning"
            customBody={
              <ul className="flex flex-col gap-space-xs text-caption">
                <li className="text-caption-emphasis">{t("page.portfolio-integrations.review.skipped")}</li>
                {skippedProjects.map((project) => (
                  <li key={project.id}>
                    {project.name}: {project.reason}
                  </li>
                ))}
              </ul>
            }
          />
        )}

        <details>
          <summary className="cursor-pointer text-caption-emphasis">
            {t("page.portfolio-integrations.review.targets", { count: validProjects.length })}
          </summary>
          <ul className="mt-space-sm max-h-scroll-area-compact overflow-y-auto text-caption text-text-secondary">
            {validProjects.map((project) => (
              <li key={project.id} className="border-b border-border-default py-space-sm last:border-b-0">
                {project.name}
              </li>
            ))}
          </ul>
        </details>
      </Stack>
    </ConfirmModal>
  );
}

type BulkOperationResultsProps = {
  results: BulkOperationResult[];
  onContinue: () => void;
  onRetry: (projectIds: string[]) => void;
  isRetrying: boolean;
};

export function BulkOperationResults({
  results,
  onContinue,
  onRetry,
  isRetrying,
}: BulkOperationResultsProps): React.ReactNode {
  const { t } = useTranslation();
  const updated = results.filter((result) => result.status === "updated");
  const skipped = results.filter((result) => result.status === "skipped");
  const failed = results.filter((result) => result.status === "failed");

  return (
    <SectionCard title={t("page.portfolio-integrations.results.title")}>
      <Inline gap="sm">
        <Badge variant="success">{t("page.portfolio-integrations.results.updated", { count: updated.length })}</Badge>
        <Badge variant={skipped.length ? "warning" : "default"}>
          {t("page.portfolio-integrations.results.skipped", { count: skipped.length })}
        </Badge>
        <Badge variant={failed.length ? "danger" : "default"}>
          {t("page.portfolio-integrations.results.failed", { count: failed.length })}
        </Badge>
      </Inline>

      {(failed.length > 0 || skipped.length > 0) && (
        <Card
          as="section"
          density="flush"
          scroll="bounded"
          aria-label={t("page.portfolio-integrations.results.title")}
          tabIndex={0}
        >
          {[...failed, ...skipped].map((result) => (
            <article key={result.projectId} className="border-b border-border-default p-space-sm last:border-b-0">
              <Spread space="sm">
                <span className="text-caption-emphasis">{result.projectName}</span>
                <Badge variant={result.status === "failed" ? "danger" : "warning"}>
                  {t(
                    result.status === "failed"
                      ? "page.portfolio-integrations.results.failed-label"
                      : "page.portfolio-integrations.results.skipped-label",
                  )}
                </Badge>
              </Spread>
              {result.reason && <p className="mt-space-xs text-caption text-text-secondary">{result.reason}</p>}
            </article>
          ))}
        </Card>
      )}

      <Form.Footer
        renderSecondaryAction={() =>
          failed.length > 0 ? (
            <Button
              variant="soft"
              isLoading={isRetrying}
              onClick={() => onRetry(failed.map((result) => result.projectId))}
            >
              {t("page.portfolio-integrations.results.retry")}
            </Button>
          ) : null
        }
        renderPrimaryAction={() => (
          <Button disabled={isRetrying} onClick={onContinue}>
            {t("page.portfolio-integrations.results.continue")}
          </Button>
        )}
      />
    </SectionCard>
  );
}
